import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Calendar, RefreshCw, Users, Briefcase, CheckCircle, XCircle, Clock } from 'lucide-react';
import { apiService } from '../services/api';
import { formatLocalYmd } from '../utils/timezoneUtils';

export type StatsPeriod = 'all' | 'last7days' | 'month' | 'year' | 'range';

export interface BusinessDashboardStats {
  summary: {
    totalAppointments: number;
    completedAppointments: number;
    cancelledAppointments: number;
    pendingAppointments: number;
    confirmedAppointments: number;
    uniqueCustomers: number;
    totalServices: number;
    activeServices: number;
    totalProfessionals: number;
    serviceBookings: number;
  };
  appointmentsByStatus: { status: string; count: number }[];
  appointmentsOverTime: { label: string; date: string; count: number }[];
  topServices: { serviceId: string; serviceName: string; count: number }[];
  period: string;
  earliestAppointmentYear: number;
}

interface BusinessStatisticsSectionProps {
  businessId: string;
  onBack: () => void;
  refreshTrigger?: number;
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  PENDING_QUOTE: 'Pending Quote',
  CONFIRMED: 'Confirmed',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-400',
  PENDING_QUOTE: 'bg-amber-400',
  CONFIRMED: 'bg-blue-500',
  COMPLETED: 'bg-green-500',
  CANCELLED: 'bg-red-400',
};

function localYmdToUtcStartIso(ymd: string): string | null {
  const parts = ymd.split('-').map((v) => parseInt(v, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toISOString();
}

function localYmdToUtcEndExclusiveIso(ymd: string): string | null {
  const parts = ymd.split('-').map((v) => parseInt(v, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

const VerticalBarChart: React.FC<{
  data: { label: string; count: number }[];
  emptyMessage?: string;
}> = ({ data, emptyMessage = 'No data for this period' }) => {
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const barAreaHeightPx = 160;

  if (data.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">{emptyMessage}</p>;
  }

  return (
    <div className="flex items-end gap-1 sm:gap-2 pt-4">
      {data.map((item, index) => {
        const barHeight =
          item.count > 0 ? Math.max((item.count / maxCount) * barAreaHeightPx, 6) : 0;
        return (
          <div key={`${item.label}-${index}`} className="flex-1 min-w-0 flex flex-col items-center gap-1">
            <span className="text-xs font-medium text-gray-700 h-4">
              {item.count > 0 ? item.count : ''}
            </span>
            <div className="w-full flex items-end" style={{ height: barAreaHeightPx }}>
              <div
                className="w-full bg-[#0B4778] rounded-t transition-all duration-300"
                style={{ height: barHeight }}
                title={`${item.label}: ${item.count}`}
              />
            </div>
            <span className="text-[10px] sm:text-xs text-gray-500 truncate w-full text-center leading-tight">
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const HorizontalBarChart: React.FC<{
  data: { label: string; count: number; color?: string }[];
  emptyMessage?: string;
}> = ({ data, emptyMessage = 'No data for this period' }) => {
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  if (data.length === 0) {
    return <p className="text-sm text-gray-500 py-4 text-center">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      {data.map((item) => {
        const widthPct = (item.count / maxCount) * 100;
        return (
          <div key={item.label} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 truncate pr-2">{item.label}</span>
              <span className="font-medium text-gray-900 flex-shrink-0">{item.count}</span>
            </div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${item.color || 'bg-[#0B4778]'}`}
                style={{ width: `${widthPct}%`, minWidth: item.count > 0 ? '4px' : '0' }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  sublabel?: string;
}> = ({ label, value, icon, sublabel }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
        {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
      </div>
      <div className="p-2 rounded-lg bg-[#0B4778]/10 text-[#0B4778]">{icon}</div>
    </div>
  </div>
);

const BusinessStatisticsSection: React.FC<BusinessStatisticsSectionProps> = ({ businessId, onBack, refreshTrigger = 0 }) => {
  const now = new Date();
  const [period, setPeriod] = useState<StatsPeriod>('last7days');
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [rangeFrom, setRangeFrom] = useState(formatLocalYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [rangeTo, setRangeTo] = useState(formatLocalYmd(now));
  const [stats, setStats] = useState<BusinessDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const params: {
        businessId: string;
        period?: string;
        year?: string;
        month?: string;
        dateFrom?: string;
        dateTo?: string;
      } = {
        businessId,
        period,
      };
      if (period === 'month') params.month = selectedMonth;
      if (period === 'year') params.year = selectedYear;
      if (period === 'range') {
        const fromIso = localYmdToUtcStartIso(rangeFrom);
        const toIso = localYmdToUtcEndExclusiveIso(rangeTo);
        if (fromIso) params.dateFrom = fromIso;
        if (toIso) params.dateTo = toIso;
      }
      const data = await apiService.getBusinessDashboardStats(params);
      setStats(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load statistics');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [businessId, period, selectedMonth, selectedYear, rangeFrom, rangeTo]);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshTrigger]);

  const yearOptions = useMemo(() => {
    const currentYear = now.getFullYear();
    const earliest = stats?.earliestAppointmentYear ?? currentYear;
    const years: number[] = [];
    for (let y = currentYear; y >= earliest; y--) {
      years.push(y);
    }
    return years;
  }, [stats?.earliestAppointmentYear, now]);

  useEffect(() => {
    if (period !== 'year' || yearOptions.length === 0) return;
    const selected = parseInt(selectedYear, 10);
    if (!Number.isFinite(selected) || !yearOptions.includes(selected)) {
      setSelectedYear(String(yearOptions[0]));
    }
  }, [period, selectedYear, yearOptions]);

  const statusChartData = useMemo(
    () =>
      (stats?.appointmentsByStatus || [])
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .map((item) => ({
          label: STATUS_LABELS[item.status] || item.status,
          count: item.count,
          color: STATUS_COLORS[item.status] || 'bg-[#0B4778]',
        })),
    [stats]
  );

  const topServicesData = useMemo(
    () =>
      (stats?.topServices || []).map((item) => ({
        label: item.serviceName,
        count: item.count,
      })),
    [stats]
  );

  const periodLabel = useMemo(() => {
    switch (period) {
      case 'all':
        return 'All time';
      case 'last7days':
        return 'Last 7 days';
      case 'month':
        return `Month: ${selectedMonth}`;
      case 'year':
        return `Year: ${selectedYear}`;
      case 'range':
        return `${rangeFrom} – ${rangeTo}`;
      default:
        return '';
    }
  }, [period, selectedMonth, selectedYear, rangeFrom, rangeTo]);

  return (
    <div className="pb-24 lg:pb-0">
      <div className="mb-4 md:mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-[#0B4778] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Appointments</span>
        </button>
      </div>

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Statistics</h2>
            <p className="text-gray-600 mt-1">
              Appointment and service insights for your business.
            </p>
          </div>
          <button
            onClick={loadStats}
            disabled={loading}
            className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 hidden md:flex items-center gap-2 self-start"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-base font-semibold text-gray-900 mb-1">Time period</div>
          <div className="text-sm text-gray-600 mb-4">Filter stats and charts by date range.</div>

          <div className="flex flex-wrap gap-2 mb-4">
            {([
              ['all', 'All time'],
              ['last7days', 'Last 7 days'],
              ['month', 'Month'],
              ['year', 'Year'],
              ['range', 'Range'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setPeriod(value)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  period === value
                    ? 'bg-[#0B4778] text-white border-[#0B4778]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {period === 'month' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
              />
            </div>
          )}

          {period === 'year' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] appearance-none pr-8 min-w-[120px]"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}

          {period === 'range' && (
            <div className="flex flex-wrap gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                />
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && !stats ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0B4778]" />
          </div>
        ) : stats ? (
          <>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Showing: {periodLabel}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Total appointments"
                  value={stats.summary.totalAppointments}
                  icon={<Calendar className="w-5 h-5" />}
                />
                <StatCard
                  label="Completed"
                  value={stats.summary.completedAppointments}
                  icon={<CheckCircle className="w-5 h-5" />}
                />
                <StatCard
                  label="Confirmed"
                  value={stats.summary.confirmedAppointments}
                  icon={<Clock className="w-5 h-5" />}
                />
                <StatCard
                  label="Cancelled"
                  value={stats.summary.cancelledAppointments}
                  icon={<XCircle className="w-5 h-5" />}
                />
                <StatCard
                  label="Unique customers"
                  value={stats.summary.uniqueCustomers}
                  icon={<Users className="w-5 h-5" />}
                />
                <StatCard
                  label="Service bookings"
                  value={stats.summary.serviceBookings}
                  icon={<Briefcase className="w-5 h-5" />}
                  sublabel="Assignments in period"
                />
                <StatCard
                  label="Active services"
                  value={stats.summary.activeServices}
                  icon={<Briefcase className="w-5 h-5" />}
                  sublabel={`${stats.summary.totalServices} total`}
                />
                <StatCard
                  label="Personnel"
                  value={stats.summary.totalProfessionals}
                  icon={<Users className="w-5 h-5" />}
                  sublabel="Can perform services"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-base font-semibold text-gray-900 mb-1">Appointments over time</div>
                <div className="text-sm text-gray-600 mb-4">Number of appointments per {period === 'year' || period === 'all' ? 'month' : 'day'}.</div>
                <VerticalBarChart data={stats.appointmentsOverTime} />
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-base font-semibold text-gray-900 mb-1">Appointments by status</div>
                <div className="text-sm text-gray-600 mb-4">Breakdown of appointment statuses in the selected period.</div>
                <HorizontalBarChart data={statusChartData} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-base font-semibold text-gray-900 mb-1">Top services</div>
              <div className="text-sm text-gray-600 mb-4">Most booked services in the selected period.</div>
              <HorizontalBarChart data={topServicesData} emptyMessage="No service bookings in this period" />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default BusinessStatisticsSection;
